import type { CSSProperties, ChangeEvent } from "react";

/**
 * Travamento de campos nos modais de cadastro de prefeituras e bancos.
 *
 * Os tres modos do modal (ver ModoModal nas telas) decidem o que fica editavel:
 *
 *   visualizar  nada e' editavel — o modal e' so consulta, sem botao de salvar.
 *   editar      tudo editavel de cara: a intencao veio do clique no lapis da
 *               tabela, entao nao ha passo intermediario.
 *   aprovar     campos oficiais travados ate o operador confirmar no modal
 *               "Editar manualmente" (components/ConfirmarEdicaoManualModal).
 *               E' o cadastro que veio do site institucional: quem preencheu foi
 *               outra pessoa, e sobrescrever a Receita precisa ser consciente.
 *
 * Motivo original da trava: algumas consultas de CNPJ voltam incompletas (codigo
 * IBGE zerado, atividade principal vazia) e travavam o formulario — a validacao
 * exige os campos e nao havia caminho pela interface pra preenche-los.
 */

/**
 * Estilo do campo travado. Cor cinza + cursor bloqueado + fundo mais escuro
 * deixam claro que nao se edita ali. Continua `readOnly` (nao `disabled`) pra
 * copia/paste seguir funcionando.
 *
 * Era duplicado em prefeituras.tsx e bancos.tsx — centralizado aqui junto com
 * os helpers que o aplicam.
 */
export const readOnlyInputStyle: CSSProperties = {
  background: "var(--bg-elev-2)",
  color: "var(--text-muted)",
  cursor: "not-allowed",
};

interface CampoEditavelProps<T> {
  value: T;
  readOnly: boolean;
  style: CSSProperties | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Props de um campo travavel de texto (TextField). Espalhe no componente:
 *
 *   <TextField label="Razão social" {...campoEditavel(camposLiberados, form.razaoSocial ?? "", (v) => setForm({ ...form, razaoSocial: v }))} />
 *
 * Serve tanto pros campos oficiais da Receita quanto pros campos livres que o
 * modo visualizar tambem precisa travar (nome do contato, agencia, dominios).
 *
 * Travado, o onChange e' no-op — o `readOnly` ja bloqueia, mas o React exige o
 * handler num input controlado.
 *
 * ATENCAO: `<select>` e `<input type="checkbox">` IGNORAM `readOnly` — esses
 * precisam de `disabled` na chamada, nao deste helper.
 */
export function campoEditavel(
  liberado: boolean,
  value: string,
  onValue: (v: string) => void,
): CampoEditavelProps<string> {
  return {
    value,
    readOnly: !liberado,
    style: liberado ? undefined : readOnlyInputStyle,
    onChange: liberado ? (e) => onValue(e.target.value) : () => undefined,
  };
}

/**
 * Variante pra NumberField (codigo IBGE). Mesmo contrato, valor numerico.
 *
 * `0` vira "" na exibicao: 0 e' o valor-sentinela de "nao preenchido" (a
 * consulta de CNPJ grava 0 quando nao acha o municipio), e mostrar um "0"
 * literal no campo faz o usuario digitar depois dele — "03550308". Vazio na
 * tela, 0 no state.
 *
 * ATENCAO: o NumberInput deixa o campo vazio na tela mas entrega 0 ao pai (ver
 * nota em packages/ui/src/web/FormField.tsx). Quem chama precisa continuar
 * bloqueando o submit quando o valor for 0 — a validacao de `ibgeInvalido` em
 * prefeituras.tsx cobre isso.
 */
export function campoEditavelNumero(
  liberado: boolean,
  value: number,
  onValue: (v: number) => void,
): CampoEditavelProps<number | ""> {
  return {
    value: value || "",
    readOnly: !liberado,
    style: liberado ? undefined : readOnlyInputStyle,
    // Descarta nao-digito antes de converter: `type="number"` barra a maior
    // parte, mas colar "3550308 " ou "35.503-08" chegaria como NaN.
    onChange: liberado ? (e) => onValue(Number(e.target.value.replace(/\D/g, "")) || 0) : () => undefined,
  };
}

// ============================================================
// Deteccao de edicao manual dos dados oficiais
// ============================================================

/** Campos que vem da Receita/Junta Comercial, achatados pra comparacao. */
export type SnapshotOficial = Record<string, string>;

/** Fonte aceita: AdminPrefeitura(Input), AdminBanco(Input) ou null. */
interface FonteOficial {
  razaoSocial?: string;
  nomeFantasia?: string;
  dataFundacao?: string;
  atividade?: string;
  uf?: string;
  municipioIbge?: number;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cep?: string;
    municipio?: string;
    uf?: string;
  };
}

// Normaliza pra comparar: undefined, "" e 0 sao todos "vazio", e espaco/caixa
// nao contam como alteracao (o operador que so reposicionou o cursor nao pode
// marcar o cadastro como editado manualmente).
const norm = (v: string | number | undefined): string =>
  v == null || v === 0 ? "" : String(v).trim().toUpperCase();

/**
 * Achata os campos oficiais de um cadastro (ou do payload da consulta CNPJ) num
 * objeto comparavel. `uf` do endereco fica separado do `uf` do topo — em
 * prefeituras sao campos distintos na tela.
 */
export function snapshotOficial(fonte: FonteOficial | null | undefined): SnapshotOficial {
  const f = fonte ?? {};
  const e = f.endereco ?? {};
  return {
    razaoSocial: norm(f.razaoSocial),
    nomeFantasia: norm(f.nomeFantasia),
    dataFundacao: norm(f.dataFundacao),
    atividade: norm(f.atividade),
    uf: norm(f.uf),
    municipioIbge: norm(f.municipioIbge),
    logradouro: norm(e.logradouro),
    numero: norm(e.numero),
    complemento: norm(e.complemento),
    bairro: norm(e.bairro),
    cep: norm(e.cep),
    municipio: norm(e.municipio),
    enderecoUf: norm(e.uf),
  };
}

/**
 * True se algum campo oficial mudou em relacao a ultima origem oficial conhecida
 * (o registro carregado ou a ultima consulta de CNPJ bem-sucedida).
 *
 * E' o que decide a flag `dadosEditadosManualmente` no save. Sem esta checagem,
 * com o modo editar abrindo destravado, TODO save marcaria o cadastro e a
 * auditoria perderia o sentido.
 */
export function oficiaisDiferem(a: SnapshotOficial, b: SnapshotOficial): boolean {
  return Object.keys(a).some((k) => a[k] !== b[k]);
}
